/* Original numbered dog artwork with licensed Google Fonts, in millimeters. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./je-pets-model.js'));
  else root.PetsRenderer = factory(root.PetsModel);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (M) {
  'use strict';
  const DPI = 600, MAX_HEIGHT = 190, SIZE = 512;
  function layoutTop(boardHeight, artworkHeight, edition, templateFamily, productId) {
    const centered = (boardHeight - artworkHeight) / 2;
    const raise = edition === 'raised-v1' && templateFamily === 'dog-face' && productId === 'stanley' ? Math.min(8, Math.max(0, centered - 5)) : 0;
    return centered - raise;
  }
  const scriptBase = typeof document === 'undefined' ? '/assets/' : new URL('./', document.currentScript?.src || document.baseURI).href;
  function canvas(width,height) { const c=document.createElement('canvas'); c.width=Math.ceil(width); c.height=Math.ceil(height); return c; }
  function timeout(promise,label) {
    let timer; return Promise.race([promise,new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error(label+' took too long to load. Retry loading.')),15000);})]).finally(()=>clearTimeout(timer));
  }
  async function load(options={}) {
    if(!M)throw new Error('Pet editor files did not load. Reload this page to retry.');
    const base=options.baseUrl || (options.catalogUrl ? new URL('./',new URL(options.catalogUrl,document.baseURI)).href : scriptBase);
    function assetUrl(value,file) {
      const raw=typeof value==='string'?value:value?.url;
      const url=new URL(raw||base+file,document.baseURI);
      if(!['http:','https:'].includes(url.protocol))throw new TypeError('Use an HTTP asset URL.'); return url.href;
    }
    if(options.catalog) M.configure(options.catalog);
    else if(!Object.keys(M.ASSETS).length || options.catalogUrl) {
      const response=await timeout(fetch(assetUrl(options.catalogUrl,'je-pets-catalog.json')),'The pet artwork catalog');
      if(!response.ok)throw new Error('The pet artwork catalog could not load. Retry loading.');
      M.configure(await response.json());
    }
    const families=new Map(),textMetrics=new Map();
    await Promise.all(Object.values(M.FONTS).map(async spec=>{
      const face=new FontFace(spec.cssFamily,'url('+JSON.stringify(assetUrl(options.fonts?.[spec.id],spec.file))+')',{weight:'400',style:'normal'});
      try {await timeout(face.load(),spec.label+' lettering');document.fonts.add(face);families.set(spec.id,spec.cssFamily);}
      catch(_){throw new Error('The '+spec.label+' lettering could not load. Retry loading.');}
    }));
    function fontStyle(ctx,fontId) {
      if(!families.has(fontId))throw new RangeError('Choose a supported Google Font.');
      ctx.font='400 '+SIZE+'px "'+families.get(fontId)+'"';ctx.textAlign='left';ctx.textBaseline='alphabetic';ctx.fontKerning='normal';ctx.fillStyle='#000';
    }
    function measureText(name,fontId,revision) {
      if(!M.fontForRevision(fontId,revision) || typeof name!=='string'||!name.trim()||name.length>M.MAX_NAME_LENGTH||M.unsupportedCharacters(name,fontId,revision).length)throw new RangeError('Enter a name supported by the selected Google Font.');
      const key=fontId+'\0'+name;if(textMetrics.has(key))return textMetrics.get(key);
      const ctx=canvas(1,1).getContext('2d',{willReadFrequently:true});fontStyle(ctx,fontId);
      const m=ctx.measureText(name),ox=Math.floor(-m.actualBoundingBoxLeft)-4,oy=Math.floor(-m.actualBoundingBoxAscent)-4;
      const w=Math.ceil(m.actualBoundingBoxRight)-ox+4,h=Math.ceil(m.actualBoundingBoxDescent)-oy+4;
      if(!(w>8&&h>8))throw new RangeError('The name needs visible lettering.');
      const resolution=Math.min(4,8192/w,4096/h,Math.sqrt(8000000/(w*h))),probe=canvas(w*resolution,h*resolution),p=probe.getContext('2d',{willReadFrequently:true});
      p.scale(resolution,resolution);fontStyle(p,fontId);p.fillText(name,-ox,-oy);
      const pixels=p.getImageData(0,0,probe.width,probe.height).data;let left=probe.width,right=-1,top=probe.height,bottom=-1;
      for(let i=3;i<pixels.length;i+=4){if(pixels[i]<32)continue;const n=(i-3)/4,x=n%probe.width,y=Math.floor(n/probe.width);left=Math.min(left,x);right=Math.max(right,x);top=Math.min(top,y);bottom=Math.max(bottom,y);}
      if(right<left)throw new RangeError('The name needs visible lettering.');
      const xMin=ox+left/resolution,yMin=oy+top/resolution,horizontalWidth=(right+1-left)/resolution,horizontalHeight=(bottom+1-top)/resolution;
      const result=Object.freeze({xMin,yMin,horizontalWidth,horizontalHeight});
      if(textMetrics.size>=400)textMetrics.clear();textMetrics.set(key,result);return result;
    }
    function paintText(ctx,design,scale) {
      const m=measureText(design.name,design.fontId,design.fontRevision),n=design.layers.name,factor=n.width/m.horizontalWidth;
      ctx.save();ctx.scale(scale,scale);ctx.translate(n.x-m.xMin*factor,n.y-m.yMin*factor);ctx.scale(factor,factor);fontStyle(ctx,design.fontId);ctx.fillText(design.name,0,0);ctx.restore();
    }
    const fonts={measure:measureText,paint:paintText};
    const images=new Map(), pending=new Map(), earFits=new Map(), earMasks=new Map();
    function measure(name,fontId='cookie',revision) {
      const m=fonts.measure(name,fontId,revision);
      return {...m,width:m.horizontalWidth,height:m.horizontalHeight,aspect:m.horizontalWidth/m.horizontalHeight,angle:0};
    }
    function earMask(spec) {
      if(earMasks.has(spec.id))return earMasks.get(spec.id);
      const source=spec.earObstacles;
      if(!source?.rows||!Number.isInteger(source.width))throw new Error('The dog ear fitting data did not load. Reload this page to retry.');
      const width=source.width,height=source.height,stride=width+1,blocked=new Uint8Array(width*height);
      // The map OR-reduces every native nonzero alpha pixel. One grid cell
      // of clearance also protects fine antialiased outlines at export size.
      for(let y=0;y<height;y++)for(let r=0;r<source.rows[y].length;r+=2) {
        const left=Math.max(0,source.rows[y][r]-1),right=Math.min(width,source.rows[y][r+1]+1);
        for(let dy=-1;dy<=1;dy++)if(y+dy>=0&&y+dy<height)blocked.fill(1,(y+dy)*width+left,(y+dy)*width+right);
      }
      const sums=new Uint16Array(stride*height);
      for(let y=0;y<height;y++){let sum=0;for(let x=0;x<width;x++){sum+=blocked[y*width+x];sums[y*stride+x+1]=sum;}}
      const result={width,height,stride,sums,crownBottom:source.crownBottom};earMasks.set(spec.id,result);return result;
    }
    function glyphRows(name,fontId,revision,width,height) {
      const probe=canvas(width+4,height+4),ctx=probe.getContext('2d',{willReadFrequently:true});
      fonts.paint(ctx,{name,fontId,fontRevision:revision,textLayout:'horizontal',layers:{name:{x:2,y:2,width,height}}},1);
      const alpha=ctx.getImageData(0,0,probe.width,probe.height).data,rows=[];
      for(let y=0;y<probe.height;y++) {
        const runs=[];let start=-1;
        for(let x=0;x<=probe.width;x++) {
          const ink=x<probe.width&&alpha[(y*probe.width+x)*4+3]>0;
          if(ink&&start<0)start=x;
          else if(!ink&&start>=0){runs.push(start-2,x-2);start=-1;}
        }
        if(runs.length)rows.push({y:y-2,runs});
      }
      return rows;
    }
    function fitEarName(spec,name,fontId,revision,metrics) {
      const key=JSON.stringify([spec.id,name,fontId,revision]);if(earFits.has(key))return earFits.get(key);
      const mask=earMask(spec),grid=mask.width,crown=mask.crownBottom;
      const shortWide=mask.height/grid<.56,deepFur=shortWide&&crown/mask.height>=.60;
      const hanging=mask.height/grid>.60&&(mask.height-crown)/grid>.37&&crown/mask.height<.45;
      // Short, deeply fringed ear shapes need quieter lettering; the long
      // hanging-ear opening needs a lower optical center. Upright ears keep
      // their larger lettering. These are source-shape rules, not name rules.
      const maxScale=Math.min(grid*(shortWide?.63:.74)/metrics.width,grid*(deepFur?.25:.30)/metrics.height);
      function clear(rows,x,y) {
        for(const row of rows) {
          const sy=y+row.y;if(sy<0||sy>=mask.height)continue;
          const base=sy*mask.stride;
          for(let r=0;r<row.runs.length;r+=2) {
            const left=Math.max(0,x+row.runs[r]),right=Math.min(grid,x+row.runs[r+1]);
            if(right>left&&mask.sums[base+right]>mask.sums[base+left])return false;
          }
        }
        return true;
      }
      let chosen=null;
      // Each pet has its own size. Scan largest-first within its optical cap,
      // then place actual glyph ink around its shape's preferred clear center.
      // A rectangular empty box would unnecessarily shrink script swashes.
      for(let step=0;step<=22&&!chosen;step++) {
        const scale=maxScale*(1-step*.025),width=metrics.width*scale,height=metrics.height*scale;
        const rows=glyphRows(name,fontId,revision,width,height);
        const first=Math.ceil(Math.max(mask.height*.16,crown-height*.30,crown+grid*.045-height*.55));
        const last=Math.floor(Math.min(mask.height+grid*.14-height,crown+grid*.25));
        const preferred=hanging?crown+grid*.047:deepFur?crown+grid*.12-height/2:first;
        const positions=[];for(let y=first;y<=last;y+=2)positions.push(y);
        positions.sort((a,b)=>Math.abs(a-preferred)-Math.abs(b-preferred)||a-b);
        for(const y of positions){if(chosen)break;for(const shift of [0,-.02,.02,-.04,.04,-.06,.06]) {
          const x=Math.round((grid-width)/2+shift*grid);
          if(x<0||x+width>grid||!clear(rows,x,y))continue;
          chosen={x:x/grid,y:y/grid,width:width/grid,height:height/grid};break;
        }}
      }
      if(!chosen) {
        // A Google Font can have different swashes from the Etsy lettering.
        // If the source opening cannot fit its real ink, keep a legible name
        // beneath the unchanged ears instead of trusting an empty rectangle.
        const scale=Math.min(grid*.70/metrics.width,grid*.25/metrics.height),width=metrics.width*scale,height=metrics.height*scale;
        chosen={x:(grid-width)/2/grid,y:mask.height/grid+.035,width:width/grid,height:height/grid};
      }
      if(earFits.size>=400)earFits.delete(earFits.keys().next().value);earFits.set(key,chosen);return chosen;
    }
    function createDesign(seed={}) {
      const templateFamily=seed.templateFamily||'dog-face', pets=M.normalizePets(seed.pets), productId=seed.productId||'owala';
      if(!Object.hasOwn(M.FAMILIES,templateFamily))throw new RangeError('Choose Dog faces or Dog ears.');
      const placementEdition=seed.placementEdition===undefined?M.defaultPlacementEdition(templateFamily,productId):M.placementEdition(seed);
      if(!M.PLACEMENT_EDITIONS.includes(placementEdition))throw new RangeError('This pet design uses an unsupported placement edition.');
      const board=M.boardForProduct(productId), targetWidth=Number(seed.targetWidth??M.defaultWidthFor(productId));
      const maximumWidth=M.maxWidthFor(productId,pets.length);
      if(!Number.isFinite(targetWidth)||targetWidth<15||targetWidth>maximumWidth)throw new RangeError('Requested artwork width must be between 15 and '+maximumWidth+' mm for this product and pet count.');
      if(!pets.length||pets.length>M.MAX_PETS)throw new RangeError('Enter one through six pets in reading order.');
      const fontId=seed.fontId||(templateFamily==='dog-face'?'chewy':'cookie'), revision=seed.fontRevision===undefined?M.FONTS[fontId]?.revision:seed.fontRevision;
      if(!M.fontForRevision(fontId,revision)?.available)throw new RangeError('Choose a supported Google Font.');
      const specs=pets.map(p=>M.assetForNumber(templateFamily,p.number));
      if(specs.some(s=>!s))throw new RangeError('Choose a numbered dog from this design family for every pet.');
      pets.forEach((pet,index)=>{
        if(typeof pet.name!=='string'||!pet.name.trim()||pet.name.length>M.MAX_NAME_LENGTH||/[\u0000-\u001f\u007f-\u009f]/.test(pet.name))throw new RangeError('Pet '+(index+1)+' needs a single-line name of 1-60 characters.');
        const missing=M.unsupportedCharacters(pet.name,fontId,revision);
        if(missing.length)throw new RangeError('The selected font does not contain these characters in pet '+(index+1)+'\'s name: '+missing.map(character=>JSON.stringify(character)).join(', ')+'.');
      });
      const measured=pets.map(p=>measure(p.name,fontId,revision));
      const horizontal=[], headHeight=targetWidth*.90/Math.max(...specs.map(s=>s.imageWidth/s.imageHeight));
      const faceNameScale=Math.min(headHeight*.24/Math.max(...measured.map(m=>m.height)),targetWidth/Math.max(...measured.map(m=>m.width)));
      let cursor=0;
      for(let i=0;i<pets.length;i++) {
        const spec=specs[i],m=measured[i],iconWidth=templateFamily==='dog-face'?headHeight*spec.imageWidth/spec.imageHeight:targetWidth;
        const iconHeight=iconWidth*spec.imageHeight/spec.imageWidth;
        const icon={x:-iconWidth/2,y:cursor,width:iconWidth,height:iconHeight};
        let name;
        if(templateFamily==='dog-face') {
          const width=m.width*faceNameScale,height=m.height*faceNameScale;
          name={x:-width/2,y:cursor+iconHeight+targetWidth*.045,width,height};
        } else {
          const fitted=fitEarName(spec,pets[i].name,fontId,revision,m);
          name={x:icon.x+fitted.x*iconWidth,y:cursor+fitted.y*iconWidth,width:fitted.width*iconWidth,height:fitted.height*iconWidth};
        }
        horizontal.push(['icon'+i,icon],['name'+i,name]);
        cursor=Math.max(icon.y+icon.height,name.y+name.height)+(i<pets.length-1?targetWidth*.10:0);
      }
      const shrink=Math.min(1,MAX_HEIGHT/cursor), left=board.width/2,top=layoutTop(board.height,cursor*shrink,placementEdition,templateFamily,productId);
      const design={version:1,designType:'pet',id:String(seed.id||'pet-design'),templateFamily,placementEdition,pets,name:pets.map(p=>p.name).join(', '),fontId,
        ...(revision!==undefined?{fontRevision:revision}:{}),productId,sizeOz:seed.sizeOz==null?null:Number(seed.sizeOz),targetWidth,board,
        layers:Object.fromEntries(horizontal.map(([key,p])=>[key,{x:left+p.x*shrink,y:top+p.y*shrink,width:p.width*shrink,height:p.height*shrink}]))};
      const errors=validate(design);if(errors.length)throw new RangeError(errors.join(' '));return design;
    }
    function validate(design) {
      const errors=M.validate(design);if(errors.length)return errors;
      for(const key of M.layerKeys(design)) {
        const text=M.textForLayer(design,key),asset=M.assetForLayer(design,key),piece=design.layers[key];
        let aspect;try {aspect=text?measure(text.text,text.fontId,text.fontRevision).aspect:asset.imageWidth/asset.imageHeight;}catch(error){errors.push(error.message);continue;}
        if(Math.abs(piece.width/piece.height/aspect-1)>.001)errors.push('The '+key+' proportions have changed. Reset the layout to restore the original shape.');
      }
      return errors;
    }
    async function prepare(design) {
      const errors=validate(design);if(errors.length)throw new RangeError(errors.join(' '));
      const specs=[...new Map(design.pets.map(p=>{const s=M.assetForNumber(design.templateFamily,p.number);return[s.id,s];})).values()];
      await Promise.all(specs.map(spec=>{
        if(images.has(spec.id))return Promise.resolve();
        if(!pending.has(spec.id)) {
          const image=new Image();image.crossOrigin='anonymous';
          const job=timeout(new Promise((resolve,reject)=>{
            image.onload=()=>{
              if(image.naturalWidth!==spec.imageWidth||image.naturalHeight!==spec.imageHeight)return reject(new Error('The supplied '+spec.label+' artwork dimensions have changed.'));
              images.set(spec.id,image);resolve();
            };
            image.onerror=()=>reject(new Error('Could not load dog '+spec.selectionNumber+' ('+spec.label+'). Retry loading.'));
            image.src=assetUrl(options.assets?.[spec.id],spec.file);
          }),spec.label+' artwork').finally(()=>pending.delete(spec.id));
          pending.set(spec.id,job);
        }
        return pending.get(spec.id);
      }));
      return design;
    }
    function drawLayer(ctx,design,key,scale=1) {
      if(!Number.isFinite(scale)||scale<=0||!M.layerKeys(design).includes(key))throw new RangeError('Choose a valid artwork piece and positive drawing scale.');
      const text=M.textForLayer(design,key),piece=design.layers[key];
      if(text)fonts.paint(ctx,{name:text.text,fontId:text.fontId,fontRevision:text.fontRevision,textLayout:'horizontal',layers:{name:piece}},scale);
      else {
        const spec=M.assetForLayer(design,key),image=images.get(spec.id);
        if(!image)throw new Error('The selected dog artwork is still loading. Prepare the design before drawing or exporting.');
        ctx.save();ctx.scale(scale,scale);ctx.drawImage(image,piece.x,piece.y,piece.width,piece.height);ctx.restore();
      }
    }
    function paint(ctx,design,scale=1){for(const key of M.layerKeys(design))drawLayer(ctx,design,key,scale);}
    function resizeForWidth(design,width){return createDesign({...design,placementEdition:M.placementEdition(design),targetWidth:Number(width)});}
    function resizeForHeight(design,height) {
      const errors=validate(design);if(errors.length)throw new RangeError(errors.join(' '));
      const bounds=M.bounds(design),targetHeight=Number(height);
      if(!Number.isFinite(targetHeight)||targetHeight<1||targetHeight>MAX_HEIGHT)throw new RangeError('Artwork height must be between 1 and 190 mm.');
      const factor=targetHeight/bounds.height,copy=M.clone(design),width=bounds.width*factor;
      if(width>M.maxWidthFor(design.productId,design.pets.length)+.001)throw new RangeError('This height would exceed the product width limit.');
      copy.targetWidth=design.targetWidth*factor;
      copy.placementEdition=M.placementEdition(design);
      const left=(copy.board.width-width)/2,top=layoutTop(copy.board.height,targetHeight,copy.placementEdition,copy.templateFamily,copy.productId);
      for(const key of M.layerKeys(design)){const p=design.layers[key];copy.layers[key]={x:left+(p.x-bounds.x)*factor,y:top+(p.y-bounds.y)*factor,width:p.width*factor,height:p.height*factor};}
      const invalid=validate(copy);if(invalid.length)throw new RangeError(invalid.join(' '));return copy;
    }
    function resizeForProduct(design,productId,sizeOz=null){return createDesign({...design,placementEdition:M.placementEdition(design),productId,sizeOz,targetWidth:Math.min(design.targetWidth,M.maxWidthFor(productId,design.pets.length))});}
    const reset=design=>createDesign({...design,placementEdition:M.placementEdition(design)});
    function fitInfo(design) {
      const bounds=M.bounds(design),automatic=reset(design),autoBounds=M.bounds(automatic);
      return {targetAxis:'width',requestedWidth:design.targetWidth,actualWidth:bounds.width,actualHeight:bounds.height,widthLimited:false,
        heightLimited:autoBounds.height>=MAX_HEIGHT-.001,
        edited:M.layerKeys(design).some(key=>['x','y','width','height'].some(field=>Math.abs(design.layers[key][field]-automatic.layers[key][field])>.001))};
    }
    function thumbnail(design,width=180,height=280) {
      const output=canvas(width,height),bounds=M.bounds(design),scale=Math.min((width-8)/bounds.width,(height-8)/bounds.height),ctx=output.getContext('2d');
      ctx.translate((width-bounds.width*scale)/2-bounds.x*scale,(height-bounds.height*scale)/2-bounds.y*scale);paint(ctx,design,scale);return output;
    }
    function renderCanvas(design,settings={}) {
      const errors=validate(design);if(errors.length)throw new RangeError(errors.join(' '));
      const scale=settings.scale??3,b=settings.fullBoard?{x:0,y:0,width:design.board.width,height:design.board.height}:M.bounds(design);
      if(!Number.isFinite(scale)||scale<=0||scale>30)throw new RangeError('Choose a positive drawing scale up to 30.');
      const output=canvas(b.width*scale,b.height*scale),ctx=output.getContext('2d',{willReadFrequently:true});ctx.translate(-b.x*scale,-b.y*scale);paint(ctx,design,scale);
      if(settings.color){ctx.setTransform(1,0,0,1,0,0);ctx.globalCompositeOperation='source-in';ctx.fillStyle=settings.color;ctx.fillRect(0,0,output.width,output.height);}
      return output;
    }
    function productionSpec(design){const b=M.bounds(design),scale=DPI/25.4;return{widthMm:design.board.width,heightMm:design.board.height,pixelWidth:Math.round(design.board.width*scale),pixelHeight:Math.round(design.board.height*scale),scale,dpi:DPI,artworkWidthMm:b.width,artworkHeightMm:b.height,artworkXMm:b.x,artworkYMm:b.y,placementEdition:M.placementEdition(design)};}
    function metadata(design){const b=M.bounds(design);return{templateFamily:design.templateFamily,placementEdition:M.placementEdition(design),productId:design.productId,sizeOz:design.sizeOz,fontId:design.fontId,fontRevision:M.designFontRevision(design),fontFamily:M.fontForDesign(design).family,pets:design.pets.map(p=>({...p})),widthMm:b.width,heightMm:b.height,bounds:{x:b.x,y:b.y,width:b.width,height:b.height},dpi:DPI,layoutEdition:'pets-google-20261103'};}
    async function exportPng(design) {
      await prepare(design);const spec=productionSpec(design),output=canvas(spec.pixelWidth,spec.pixelHeight);
      paint(output.getContext('2d',{willReadFrequently:true}),design,spec.scale);
      const blob=await new Promise((resolve,reject)=>output.toBlob(value=>value?resolve(value):reject(new Error('PNG export failed.')),'image/png'));
      return new Blob([withDpi(new Uint8Array(await blob.arrayBuffer()),DPI)],{type:'image/png'});
    }
    return Object.freeze({createDesign,prepare,measure,validate,paint,drawLayer,renderCanvas,productionSpec,metadata,exportPng,resizeForWidth,resizeForHeight,resizeForProduct,reset,fitInfo,thumbnail,
      assetInfo:Object.freeze({dpi:DPI,fonts:M.FONTS,icons:M.ASSETS}),loadedAssetCount:()=>images.size});
  }
  function crc32(bytes) {
    let crc = 0xffffffff;
    for (const byte of bytes) { crc ^= byte; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); }
    return (crc ^ 0xffffffff) >>> 0;
  }
  function withDpi(png, dpi) {
    if (!(png instanceof Uint8Array) || png.length < 33 || ![137,80,78,71,13,10,26,10].every((byte, i) => png[i] === byte)) throw new TypeError('A valid PNG is required.');
    const density = Math.round(dpi / 0.0254), phys = new Uint8Array(21), view = new DataView(phys.buffer);
    view.setUint32(0, 9); phys.set([112,72,89,115], 4); view.setUint32(8, density); view.setUint32(12, density); phys[16] = 1;
    view.setUint32(17, crc32(phys.subarray(4, 17)));
    const chunks = [png.subarray(0, 8)], input = new DataView(png.buffer, png.byteOffset, png.byteLength);
    let offset = 8, inserted = false, ended = false;
    while (offset + 12 <= png.length) {
      const length = input.getUint32(offset), end = offset + length + 12;
      if (end > png.length) throw new TypeError('The PNG has an incomplete chunk.');
      const type = String.fromCharCode(...png.subarray(offset + 4, offset + 8));
      if (type !== 'pHYs') chunks.push(png.subarray(offset, end));
      if (type === 'IHDR') { chunks.push(phys); inserted = true; }
      offset = end; if (type === 'IEND') { ended = true; break; }
    }
    if (!inserted || !ended) throw new TypeError('The PNG is missing its header or end chunk.');
    const result = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0)); let cursor = 0;
    for (const chunk of chunks) { result.set(chunk, cursor); cursor += chunk.length; } return result;
  }
  return Object.freeze({load,withDpi});
});
